// Common plan designs offered as starting points. Shared by the Presets dialog
// in the plan-settings pane and the first-run onboarding flow.

import { CASH_BALANCE_SUPPORTED_PLAN_YEAR } from './constants.js'
import { EVERYONE_ELSE_GROUP_ID } from './planDesign.js'
import { classIdForLabel } from './allocationClasses.js'

// Allocation classes are census codes, never invented by a preset: a class only
// has members when the `allocation_group` column carries that label. The
// bundled example census codes owners as "Owner", so a preset keyed on that id
// lands on real people out of the box, and on nobody (leaving the catch-all
// rate to cover everyone) against a census that does not use the column.
const OWNER_CLASS_LABEL = 'Owner'
const OWNER_GROUP_ID = classIdForLabel(OWNER_CLASS_LABEL)

const profitSharingPreset = (overrides = {}) => ({
  mode: 'standard',
  enabled: false,
  funding: { mode: 'rate' },
  formula: { type: 'pro_rata', percent: 0 },
  conditions: { minHours: 0, lastDayRequired: false, combinator: 'and', waivers: null },
  ...overrides,
})

const sixYearGradedSchedule = () => ({
  type: 'graded',
  schedule: [
    { years: 0, percent: 0 },
    { years: 1, percent: 0 },
    { years: 2, percent: 20 },
    { years: 3, percent: 40 },
    { years: 4, percent: 60 },
    { years: 5, percent: 80 },
    { years: 6, percent: 100 },
  ],
})

const employerPreset = (overrides = {}) => ({
  matchEnabled: false,
  // Presets own the complete employer-contribution slice. Always return to a
  // simple tiered match unless the preset explicitly authors grouped formulas;
  // otherwise a prior grouped design can leave matchMode + empty matchGroups
  // behind and make a visibly enabled match calculate as $0.
  matchMode: 'simple',
  matchTiers: [{ matchPercent: 100, upToPercent: 1 }, { matchPercent: 50, upToPercent: 6 }],
  matchGroups: [],
  matchCapPerEmployee: 0,
  necEnabled: false,
  necPercent: 3,
  necIsSafeHarbor: true,
  profitSharing: profitSharingPreset(),
  ...overrides,
})

const vestingPreset = (overrides = {}) => ({
  enabled: false,
  matchSchedule: { type: 'immediate' },
  necSchedule: { type: 'immediate' },
  profitSharingSchedule: { type: 'immediate' },
  ...overrides,
})

const afterTaxPreset = (overrides = {}) => ({
  allowed: false,
  maxPercent: 0,
  maxDollar: 0,
  ...overrides,
})

// A preset fully defines a plan, so it also resets cash balance to a known state.
// Non-cash-balance presets pass no overrides and land on the disabled default;
// the cash-balance combo preset turns it on with a starter credit.
const cashBalancePreset = (overrides = {}) => ({
  mode: 'structured',
  enabled: false,
  credits: [],
  interestCredit: { type: 'fixed', rate: 5 },
  ...overrides,
})

// A starter cash-balance credit: a 5%-of-pay hypothetical benefit credit for
// everyone, with fixed-interest projection assumptions. Cash balance only
// computes for the supported plan year, so the combo preset moves the plan there.
// A uniform 5% credit. The owner class is listed explicitly alongside the
// catch-all: a class with no formula receives NOTHING (classes never borrow
// each other's credits), so relying on the catch-all alone would leave owners
// out of the very plan a cash-balance design exists to fund.
const cashBalanceComboSettings = () => ({
  enabled: true,
  credits: [
    { groupId: OWNER_GROUP_ID, groupLabel: OWNER_CLASS_LABEL, formula: { method: 'percentOfPay', value: 5 } },
    { groupId: EVERYONE_ELSE_GROUP_ID, formula: { method: 'percentOfPay', value: 5 } },
  ],
})

// Owner-weighted cash balance: a large flat credit for the owner class and a
// small percent-of-pay credit for everyone else. The staff credit is what makes
// §401(a)(26) reachable, and it counts toward the combined gateway.
const cashBalanceOwnerMaxSettings = () => ({
  enabled: true,
  credits: [
    { groupId: OWNER_GROUP_ID, groupLabel: OWNER_CLASS_LABEL, formula: { method: 'flatDollar', value: 150000 } },
    { groupId: EVERYONE_ELSE_GROUP_ID, formula: { method: 'percentOfPay', value: 2 } },
  ],
})

// New comparability: one rate per allocation class, tested on a benefits basis.
// The staff rate sits at the 5% minimum allocation gateway, which is the floor
// that lets the design be cross-tested at all.
const newComparabilitySettings = ({ ownerFormula, staffPercent = 5 } = {}) => ({
  enabled: true,
  funding: { mode: 'maximize', targets: { rule: 'all-owners' } },
  formula: {
    type: 'new_comparability',
    groupMode: 'grouped',
    groups: [
      { groupId: OWNER_GROUP_ID, groupLabel: OWNER_CLASS_LABEL, formula: ownerFormula },
      { groupId: EVERYONE_ELSE_GROUP_ID, groupLabel: '', formula: { method: 'percent', value: staffPercent } },
    ],
  },
})

export const PLAN_PRESETS = [
  {
    name: 'Employee Deferrals Only',
    description: 'No employer contribution.',
    apply: (update) => update({
      employerContributions: employerPreset(),
      vesting: vestingPreset(),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(),
    }),
  },
  {
    name: 'Basic Match',
    description: '50% to 4%; 3-year cliff vesting.',
    apply: (update) => update({
      employerContributions: employerPreset({ matchEnabled: true, matchTiers: [{ matchPercent: 50, upToPercent: 4 }] }),
      vesting: vestingPreset({ enabled: true, matchSchedule: { type: 'cliff', years: 3 } }),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(),
    }),
  },
  {
    name: 'Safe Harbor Nonelective',
    description: '3% safe harbor NEC; immediate vesting.',
    apply: (update) => update({
      employerContributions: employerPreset({ necEnabled: true, necPercent: 3, necIsSafeHarbor: true }),
      vesting: vestingPreset({ enabled: true }),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(),
    }),
  },
  {
    name: 'QACA Safe Harbor Match',
    description: 'QACA match; 2-year cliff vesting.',
    apply: (update) => update({
      employerContributions: employerPreset({ matchEnabled: true, matchTiers: [{ matchPercent: 100, upToPercent: 1 }, { matchPercent: 50, upToPercent: 6 }] }),
      vesting: vestingPreset({ enabled: true, matchSchedule: { type: 'cliff', years: 2 } }),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(),
    }),
  },
  {
    name: 'Traditional Match',
    description: '100% to 3%, then 50% to 5%; graded vesting.',
    apply: (update) => update({
      employerContributions: employerPreset({ matchEnabled: true, matchTiers: [{ matchPercent: 100, upToPercent: 3 }, { matchPercent: 50, upToPercent: 5 }] }),
      vesting: vestingPreset({ enabled: true, matchSchedule: sixYearGradedSchedule() }),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(),
    }),
  },
  {
    name: 'Enhanced Match + After-Tax',
    description: 'Enhanced match; immediate vesting; after-tax enabled.',
    // Matches the store's Plan A default (see createPlanA in useStore.js), so
    // onboarding can offer "keep the default" honestly by pointing here.
    isStoreDefault: true,
    apply: (update) => update({
      employerContributions: employerPreset({ matchEnabled: true, matchTiers: [{ matchPercent: 100, upToPercent: 4 }, { matchPercent: 50, upToPercent: 6 }] }),
      vesting: vestingPreset({ enabled: true }),
      afterTax: afterTaxPreset({ allowed: true, maxDollar: 15000 }),
      cashBalance: cashBalancePreset(),
    }),
  },
  {
    name: 'Enhanced Match + NEC + Profit Sharing',
    description: 'Enhanced match, 3% NEC, and 1% profit sharing.',
    apply: (update) => update({
      employerContributions: employerPreset({
        matchEnabled: true,
        matchTiers: [{ matchPercent: 100, upToPercent: 4 }, { matchPercent: 50, upToPercent: 6 }],
        necEnabled: true,
        necPercent: 3,
        necIsSafeHarbor: true,
        profitSharing: profitSharingPreset({
          enabled: true,
          formula: { type: 'pro_rata', percent: 1 },
        }),
      }),
      vesting: vestingPreset({ enabled: true, profitSharingSchedule: sixYearGradedSchedule() }),
      afterTax: afterTaxPreset({ allowed: true, maxDollar: 20000 }),
      cashBalance: cashBalancePreset(),
    }),
  },
  // New comparability: the owner class fills its §415(c) headroom while staff
  // sit at the gateway. Only legal on a benefits basis, so the design lives or
  // dies on the cross-testing screen.
  {
    name: 'New Comparability (Owner-Weighted)',
    description: 'Owner-weighted profit sharing; cross-testing required.',
    apply: (update) => update({
      employerContributions: employerPreset({
        necEnabled: true,
        necPercent: 3,
        necIsSafeHarbor: true,
        profitSharing: profitSharingPreset(newComparabilitySettings({
          ownerFormula: { method: 'fill_415c' },
        })),
      }),
      vesting: vestingPreset({ enabled: true, profitSharingSchedule: sixYearGradedSchedule() }),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(),
    }),
  },
  // Cash-balance combo: a defined-benefit credit stacked on a safe-harbor DC
  // base, using only the supported explicit-credit workflow.
  {
    name: 'Safe Harbor + Cash Balance',
    description: '3% NEC plus a 5%-of-pay cash balance credit.',
    apply: (update) => update({
      planYear: CASH_BALANCE_SUPPORTED_PLAN_YEAR,
      employerContributions: employerPreset({ necEnabled: true, necPercent: 3, necIsSafeHarbor: true }),
      vesting: vestingPreset({ enabled: true }),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(cashBalanceComboSettings()),
    }),
  },
  // The full owner-max design: a large owner cash-balance credit, a small staff
  // credit that keeps §401(a)(26) reachable and feeds the combined gateway, and
  // new-comparability profit sharing on top. This is the most aggressive preset
  // and the one that most needs an actuary.
  {
    name: 'Owner-Max Cash Balance + New Comparability',
    description: '$150,000 owner cash balance credit plus new comparability.',
    apply: (update) => update({
      planYear: CASH_BALANCE_SUPPORTED_PLAN_YEAR,
      employerContributions: employerPreset({
        necEnabled: true,
        necPercent: 3,
        necIsSafeHarbor: true,
        profitSharing: profitSharingPreset(newComparabilitySettings({
          ownerFormula: { method: 'fill_415c' },
          // A top-heavy DB/DC aggregation group owes non-key participants a 5%
          // DC minimum under the §1.416-1 M-12 safe harbor, and the 3% safe
          // harbor above covers only part of it. Two points of profit sharing
          // closes the gap without assuming the plan is top-heavy.
          staffPercent: 2,
        })),
      }),
      vesting: vestingPreset({ enabled: true, profitSharingSchedule: sixYearGradedSchedule() }),
      afterTax: afterTaxPreset(),
      cashBalance: cashBalancePreset(cashBalanceOwnerMaxSettings()),
    }),
  },
]

export const getPresetSettings = (preset) => {
  let captured = {}
  preset.apply((updates) => { captured = { ...captured, ...updates } })
  return JSON.parse(JSON.stringify(captured))
}

const formatMatchTiers = (tiers) => tiers.map((t) => `${t.matchPercent}% up to ${t.upToPercent}% of pay`).join(', ')

const formatVestingSchedule = (schedule) => {
  if (!schedule || schedule.type === 'immediate') return 'Immediate'
  if (schedule.type === 'cliff') return `${schedule.years}-year cliff`
  if (schedule.type === 'graded') {
    const last = schedule.schedule?.[schedule.schedule.length - 1]
    return `${last?.years ?? 6}-year graded`
  }
  return 'Immediate'
}

const describeCreditFormula = (formula = {}) => (formula.method === 'flatDollar'
  ? `$${(formula.value || 0).toLocaleString()}`
  : Number.isFinite(formula.value) ? `${formula.value}% of pay` : 'to be set')

// Every credit class, not just the first: an owner-weighted design is only
// legible when the staff credit is on the page next to the owner credit.
const describeCashBalance = (cb) => {
  const credits = cb.credits || []
  if (!credits.length) return `Credit to be set · ${cb.interestCredit?.rate ?? 5}% interest`
  const parts = credits.map((credit) => {
    const label = String(credit.groupLabel || '').trim()
      || (credit.groupId === EVERYONE_ELSE_GROUP_ID ? 'Everyone else' : '')
    const amount = describeCreditFormula(credit.formula)
    return label ? `${label} ${amount}` : amount
  })
  return `${parts.join(' · ')} · ${cb.interestCredit?.rate ?? 5}% interest`
}

const describeProfitSharingGroups = (formula = {}) => (formula.groups || [])
  .map((group) => {
    const label = String(group.groupLabel || '').trim()
      || (group.groupId === EVERYONE_ELSE_GROUP_ID ? 'Everyone else' : group.groupId)
    const rate = group.formula?.method === 'fill_415c'
      ? 'to the §415(c) limit'
      : group.formula?.method === 'flat'
        ? `$${(group.formula.value || 0).toLocaleString()}`
        : `${group.formula?.value || 0}% of pay`
    return `${label} ${rate}`
  })
  .join(' · ')

export const presetSettingRows = (preset) => {
  const { employerContributions: ec, vesting: v, afterTax: at, cashBalance: cb } = getPresetSettings(preset)
  const rows = []

  rows.push({ label: 'Employer match', value: ec.matchEnabled ? formatMatchTiers(ec.matchTiers) : 'None' })
  rows.push({ label: 'Nonelective (NEC)', value: ec.necEnabled ? `${ec.necPercent}% of pay${ec.necIsSafeHarbor ? ' (safe harbor)' : ''}` : 'None' })
  rows.push({
    label: 'Profit sharing',
    value: ec.profitSharing?.enabled
      ? (ec.profitSharing.formula?.type === 'age_weighted'
          ? 'Age-weighted (cross-tested)'
          : ec.profitSharing.formula?.type === 'new_comparability'
            ? `New comparability · ${describeProfitSharingGroups(ec.profitSharing.formula)}`
            : ec.profitSharing.formula?.type === 'flat_dollar'
              ? `$${(ec.profitSharing.formula.amount || 0).toLocaleString()}`
              : `${ec.profitSharing.formula?.percent || 0}% of pay`)
      : 'None',
  })
  if (v.enabled) {
    if (ec.matchEnabled) rows.push({ label: 'Match vesting', value: formatVestingSchedule(v.matchSchedule) })
    if (ec.necEnabled) rows.push({ label: 'NEC vesting', value: formatVestingSchedule(v.necSchedule) })
    if (ec.profitSharing?.enabled) rows.push({ label: 'Profit sharing vesting', value: formatVestingSchedule(v.profitSharingSchedule) })
  } else {
    rows.push({ label: 'Vesting', value: 'Immediate' })
  }

  rows.push({
    label: 'After-tax / MBDR',
    value: at.allowed
      ? `Up to $${(at.maxDollar || 0).toLocaleString()}`
      : 'Not allowed',
  })

  if (cb?.enabled) {
    rows.push({ label: 'Cash balance', value: describeCashBalance(cb) })
  }

  return rows
}
